import { type FC, useMemo, useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { useTimeoutFn } from 'react-use'
import { useGetAccountQuery, useUpdateAccountMutation } from '../../../../../store/accountUserSettings/accountUserSettings'
import { useGetAllUsersQuery } from '../../../../../store/user/user'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useZocksAuth } from '../../../../../components/ScopedBlock/hooks'
import { Button } from '../../../../../components/Button/Button'
import { sanitizeAccountConfig } from '../sanitizeAccountConfig'

interface FormValues {
  businessAddress: string
}

export const BusinessAddressSettings: FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { hasScope } = useZocksAuth()
  const canEdit = hasScope('**', 'account:update', ['account:admin'])
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: isSaving }] = useUpdateAccountMutation()
  const { data: usersData, isFetching: isUsersLoading } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] }, { refetchOnMountOrArgChange: true })
  const validUserIds = useMemo(() => (usersData && !isUsersLoading ? new Set(usersData.data.map((user) => user.id)) : null), [usersData, isUsersLoading])
  const [showChangesSaved, setShowChangesSaved] = useState<boolean>(false)
  const [, , resetChangesSavedTimer] = useTimeoutFn(() => setShowChangesSaved(false), 2000)

  const persistedBusinessAddress = account?.data.config.businessAddress ?? ''

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({ values: { businessAddress: persistedBusinessAddress }, resetOptions: { keepDirtyValues: true } })

  const onCancel = () => {
    reset({ businessAddress: persistedBusinessAddress }, { keepDirtyValues: false })
  }

  const onSubmit = async ({ businessAddress }: FormValues) => {
    if (!account) {
      return
    }
    const trimmedBusinessAddress = businessAddress.trim()
    const result = await updateAccount({
      accountId,
      payload: {
        config: sanitizeAccountConfig({ ...account.data.config, businessAddress: trimmedBusinessAddress === '' ? null : trimmedBusinessAddress }, validUserIds),
      },
    })
    if ('data' in result) {
      reset({ businessAddress: trimmedBusinessAddress }, { keepDirtyValues: false })
      setShowChangesSaved(true)
      resetChangesSavedTimer()
    }
  }

  const actionsDisabled = account == null || isSaving || !isDirty

  return (
    <Container>
      <Row className='zocks-mb-2'>
        <Col>
          <h4 className='zocks-text-gray-900'>Business address</h4>
        </Col>
      </Row>
      <Row className='zocks-mb-1.25'>
        <Col>
          <p className='paragraph-medium zocks-text-gray-900 p-0 m-0'>
            Zocks adds this to emails sent to clients, like booking invitations, where US email rules expect a physical address. Emails are still sent if you
            leave this empty. Not sure which address to use? Check with your compliance team.
          </p>
        </Col>
      </Row>
      {canEdit ? (
        <>
          <Row className='mb-1'>
            <Col>
              <label htmlFor='businessAddress' className='paragraph-medium'>
                Business address
              </label>
            </Col>
          </Row>
          <Row>
            <Col className='pe-2'>
              <Controller
                name='businessAddress'
                control={control}
                render={({ field }) => (
                  <Form.Control
                    id={field.name}
                    type='text'
                    {...field}
                    placeholder='Street, city, state, ZIP'
                    disabled={account == null || isSaving}
                    maxLength={500}
                  />
                )}
              />
              {showChangesSaved ? (
                <div className='label-small zocks-text-success-200 d-flex gap-2 align-items-center mt-1'>
                  <i className='bi bi-check-lg fs-6' />
                  <span>Changes saved</span>
                </div>
              ) : null}
            </Col>
            <Col xs='auto' className='ps-2'>
              <div className='d-flex gap-2'>
                <Button variant='secondary' disabled={actionsDisabled} onClick={onCancel}>
                  Cancel
                </Button>
                <Button variant='primary' disabled={actionsDisabled} loading={isSaving} onClick={handleSubmit(onSubmit)}>
                  Save
                </Button>
              </div>
            </Col>
          </Row>
        </>
      ) : (
        <Row>
          <Col>
            <BusinessAddressCard businessAddress={persistedBusinessAddress} />
            <p className='paragraph-medium zocks-text-gray-900 p-0 m-0 zocks-mt-1.25'>Managed at the account level.</p>
          </Col>
        </Row>
      )}
    </Container>
  )
}

interface BusinessAddressCardProps {
  businessAddress: string
}

const BusinessAddressCard: FC<BusinessAddressCardProps> = ({ businessAddress }) => (
  <div className='d-flex flex-column zocks-gap-0.25 zocks-p-0.75 rounded-3 border zocks-border-color-gray-200 zocks-bg-color-gray-100'>
    <div className='lead-extra-small zocks-text-gray-600'>Business address</div>
    <span className='paragraph-medium medium zocks-text-gray-900'>{businessAddress === '' ? 'Not set' : businessAddress}</span>
  </div>
)
